import React from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const ChartModal = ({ metric, onClose }) => {
  if (!metric) {
    return null;
  }

  // Create array of 52 weeks, filling in missing weeks with 0
  const generateWeeklyLabels = () => {
    if (!metric.weeklyData || metric.weeklyData.length === 0) {
      return [];
    }

    // Find the earliest and latest dates in the data
    const dates = metric.weeklyData.map((week) => new Date(week.date));
    const earliestDate = new Date(Math.min(...dates));
    const latestDate = new Date(Math.max(...dates));

    // Generate labels starting from the earliest date
    const weeks = [];
    let currentDate = new Date(earliestDate);

    while (currentDate <= latestDate) {
      weeks.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 7);
    }

    return weeks;
  };

  const weekLabels = generateWeeklyLabels();
  const labels = weekLabels.map((date) => {
    // Format date to match the data format (YYYY-MM-DD)
    // Add one day to match the data's Sunday-based weeks
    const adjustedDate = new Date(date);
    adjustedDate.setDate(adjustedDate.getDate() + 1);
    const year = adjustedDate.getFullYear();
    const month = String(adjustedDate.getMonth() + 1).padStart(2, "0");
    const day = String(adjustedDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  });

  const getDatasets = () => {
    // Create a map of existing data points by date string
    const dataMap = new Map();

    if (metric.weeklyData && Array.isArray(metric.weeklyData)) {
      metric.weeklyData.forEach((week) => {
        if (week && week.date) {
          // Use the date directly from the data
          dataMap.set(week.date, week.value || 0);
        }
      });
    }

    const weeklyValues = labels.map((dateStr) => {
      const value = dataMap.get(dateStr);
      return value !== undefined ? value : 0;
    });

    const colors = getMetricColor(metric.name);

    return [
      {
        label: metric.name,
        data: weeklyValues,
        borderColor: colors.border,
        backgroundColor: colors.background,
        tension: 0.3,
        borderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ];
  };

  const getMetricColor = (metricName) => {
    switch (metricName) {
      case "Total Revenue":
        return {
          border: "rgb(34, 197, 94)",
          background: "rgba(34, 197, 94, 0.1)",
        };
      case "Total Appointments":
        return {
          border: "rgb(59, 130, 246)",
          background: "rgba(59, 130, 246, 0.1)",
        };
      case "New Clients":
        return {
          border: "rgb(249, 115, 22)",
          background: "rgba(249, 115, 22, 0.1)",
        };
      case "Consult":
        return {
          border: "rgb(168, 85, 247)",
          background: "rgba(168, 85, 247, 0.1)",
        };
      default:
        return {
          border: "rgb(107, 114, 128)",
          background: "rgba(107, 114, 128, 0.1)",
        };
    }
  };

  const data = {
    labels: weekLabels.map((date) => {
      // Add one day to match the data's Sunday-based weeks
      const adjustedDate = new Date(date);
      adjustedDate.setDate(adjustedDate.getDate() + 1);
      return adjustedDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }),
    datasets: getDatasets(),
  };

  const formatYAxisLabel = (value, metricName) => {
    switch (metricName) {
      case "Total Revenue":
        return "$" + value.toFixed(2);
      default:
        return Math.round(value);
    }
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
          },
        },
      },
      title: {
        display: true,
        text: `${metric.name} - Weekly Trends`,
        font: {
          size: 16,
          weight: 'bold',
        },
        padding: {
          top: 10,
          bottom: 30,
        },
      },
    },
    scales: {
      x: {
        type: "category",
        title: {
          display: true,
          text: "Week",
          font: {
            size: 12,
            weight: 'bold',
          },
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          font: {
            size: 11,
          },
        },
        grid: {
          color: 'rgba(107, 114, 128, 0.1)',
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: metric.name.includes('Revenue') ? 'Amount ($)' : 'Count',
          font: {
            size: 12,
            weight: 'bold',
          },
        },
        ticks: {
          callback: function (value) {
            return formatYAxisLabel(value, metric.name);
          },
          stepSize: Math.ceil(Math.max(...getDatasets()[0].data) / 5), // Divide y-axis into ~5 steps
          font: {
            size: 11,
          },
        },
        grid: {
          color: 'rgba(107, 114, 128, 0.1)',
        },
      },
    },
  };

  const formatValue = (value, metricName) => {
    if (value === undefined || value === null || value === "") return "N/A";

    switch (metricName) {
      case "Total Revenue":
        return typeof value === "string" && value.startsWith("$")
          ? value
          : `$${parseFloat(value).toFixed(2)}`;
      default:
        return value.toString();
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-sm shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{metric.name} Analysis</h2>
              <p className="text-sm text-gray-500 mt-0.5">Weekly performance trends and metrics</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-sm transition-colors">
              <span className="sr-only">Close</span>
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="h-80 mb-6">
            <Line options={options} data={data} />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="bg-gray-50 border border-gray-200 rounded-sm p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Current Performance</h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">This Week:</span>
                  <span className="text-sm font-medium text-gray-900">{formatValue(metric.thisWeek, metric.name)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Last Week:</span>
                  <span className="text-sm font-medium text-gray-900">{formatValue(metric.lastWeek, metric.name)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Previous Week:</span>
                  <span className="text-sm font-medium text-gray-900">{formatValue(metric.prevWeek, metric.name)}</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                  <span className="text-sm text-gray-600">Month to Date:</span>
                  <span className="text-sm font-semibold text-gray-900">{formatValue(metric.mtd, metric.name)}</span>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 border border-gray-200 rounded-sm p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Historical Performance</h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Last Month:</span>
                  <span className="text-sm font-medium text-gray-900">{formatValue(metric.lastMonth, metric.name)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Target:</span>
                  <span className="text-sm font-medium text-gray-900">{formatValue(metric.target, metric.name)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Current Achievement:</span>
                  <span className="text-sm font-medium text-gray-900">{metric.percentCurr || 'N/A'}</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                  <span className="text-sm text-gray-600">Previous Achievement:</span>
                  <span className="text-sm font-semibold text-gray-900">{metric.percentLast || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChartModal;
